import { useState, useRef, FormEvent } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../../contexts/language/LanguageContext';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useSEO } from '../../hooks/useSEO';
import { CONTACT_INFO, INTERSECTION_THRESHOLDS } from '../../helpers/constants.helper';
import { EMAIL_JS_CONFIG, RECAPTCHA_CONFIG } from '../../config/credentials.config';
import './ContactPage.scss';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name: boolean;
  email: boolean;
  message: boolean;
}

const ContactPage = () => {
  const { t } = useLanguage();
  const [intersectionRef, isVisible] = useIntersectionObserver({
    threshold: INTERSECTION_THRESHOLDS.VERY_HIGH
  });

  // SEO optimization
  useSEO({
    title: 'Contact - ALINA POLONEYEVA',
    description: 'Get in touch with Ukrainian visual creator Alina Poloneyeva for modeling collaborations, fashion content creation projects, and brand partnerships. Available in Warsaw and Paris.',
    keywords: 'contact Alina Poloneyeva, Ukrainian model booking, collaboration inquiries, brand partnerships, fashion content services, Warsaw model contact, Paris model contact',
    ogTitle: 'Contact Alina Poloneyeva - Modeling & Content Creation',
    ogDescription: 'Get in touch for modeling collaborations and fashion content creation projects',
    ogImage: 'https://alinapoloneyeva.com/images/about/about.jpg',
    ogUrl: 'https://alinapoloneyeva.com/contact',
    canonical: 'https://alinapoloneyeva.com/contact',
  });

  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({
    name: false,
    email: false,
    message: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleEmailBlur = () => {
    if (formData.email.trim() !== '' && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: true }));
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    if (token) {
      setRecaptchaError(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      name: formData.name.trim() === '',
      email: formData.email.trim() === '' || !validateEmail(formData.email),
      message: formData.message.trim() === ''
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!recaptchaToken) {
      setRecaptchaError(true);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await emailjs.send(
        EMAIL_JS_CONFIG.serviceId,
        EMAIL_JS_CONFIG.templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_email: CONTACT_INFO.email,
          'g-recaptcha-response': recaptchaToken
        },
        EMAIL_JS_CONFIG.publicKey
      );

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact" ref={intersectionRef}>
      <div className={`contact__container ${isVisible ? 'visible' : ''}`}>
        <h2 className="contact__title">{t.contact.title}</h2>

        <p className="contact__description">
          {t.contact.description}
        </p>

        <form ref={formRef} className="contact__form" onSubmit={handleSubmit}>
          <div className="contact__form-grid">
            <div className={`contact__form-group ${errors.name ? 'error' : ''}`}>
              <label htmlFor="name" className="contact__label">
                {t.contact.form.name}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="contact__input"
                placeholder={t.contact.form.namePlaceholder}
              />
              {errors.name && (
                <span className="contact__error">{t.contact.form.nameError}</span>
              )}
            </div>

            <div className={`contact__form-group ${errors.email ? 'error' : ''}`}>
              <label htmlFor="email" className="contact__label">
                {t.contact.form.email}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                className="contact__input"
                placeholder={t.contact.form.emailPlaceholder}
              />
              {errors.email && (
                <span className="contact__error">{t.contact.form.emailError}</span>
              )}
            </div>
          </div>

          <div className={`contact__form-group ${errors.message ? 'error' : ''}`}>
            <label htmlFor="message" className="contact__label">
              {t.contact.form.message}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="contact__textarea"
              placeholder={t.contact.form.messagePlaceholder}
              rows={6}
            />
            {errors.message && (
              <span className="contact__error">{t.contact.form.messageError}</span>
            )}
          </div>

          <div className={`contact__recaptcha-wrapper ${recaptchaError ? 'error' : ''}`}>
            <div className="contact__recaptcha">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_CONFIG.siteKey}
                onChange={handleRecaptchaChange}
                theme="light"
              />
            </div>
            {recaptchaError && (
              <span className="contact__error contact__error--recaptcha">
                {t.contact.form.recaptchaError}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="contact__submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? t.contact.form.sending : t.contact.form.submit}
          </button>

          {submitStatus === 'success' && (
            <p className="contact__message contact__message--success">
              {t.contact.form.successMessage}
            </p>
          )}

          {submitStatus === 'error' && (
            <p className="contact__message contact__message--error">
              {t.contact.form.errorMessage}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactPage;